import { useState, useEffect} from 'react';
import { 
  Check, 
  CloudRain, Package, Music, MapPin, Award, 
  Activity
} from 'lucide-react';

export function InnovationHub() {
  const [selected, setSelected] = useState({});
  const [mode, setMode] = useState('manual'); // 'manual' or 'auto'
  const [sliderValue, setSliderValue] = useState(20);

  // The full list of tools based on your HTML
  const tools = [
    { id: 1, icon: "🌧️", title: "Hyper-Local Context", header: "Weather Trigger: ", desc: "If it rains, screens instantly promote umbrellas or hot drinks." },
    { id: 2, icon: "📢", title: "External Retail Media", header: "Ad Monetization: ", desc: "Monetize idle screen time with programmatic ads.", hasSlider: true },
    { id: 3, icon: "📈", title: "Realtime Sales Trends", header: "Buzz Detector: ", desc: "Identifies abnormal demand spikes (e.g., +200% sales in 1 hour) to auto-generate \"Trending Now\" signage." },
    { id: 4, icon: "🔥", title: "Social Media Trends", header: "Viral Loop: ", desc: "Automatically feature trending products or challenges from TikTok/Instagram." },
    { id: 5, icon: "📅", title: "Special Dates & Events", header: "Calendar Auto-Pilot: ", desc: "Automates content for holidays, local festivals, and seasonal milestones." },
    { id: 6, icon: "📦", title: "Inventory Integration", header: "POS Link: ", desc: "\"Product A\" expiring? Agent triggers a \"Flash Sale\" audio spot immediately." },
    { id: 7, icon: "🎵", title: "Dynamic Music", header: "Sonic Branding: ", desc: "Fit the in-store music playlist and tempo to your specific marketing goals." },
    { id: 8, icon: "🤖", title: "AI Concierge", header: "Smart-Cart: ", desc: "\"Where is the gluten-free pasta?\" - Answers via map & inventory data directly on the cart screen." },
    { id: 9, icon: "🏆", title: "Gamification", header: "Scavenger Hunt: ", desc: "\"Find 3 golden QR codes.\" The Agent generates clues and assets." },
    { id: 10, icon: "🚦", title: "In-Store Traffic Reaction", header: "Using real-time traffic data: ", desc: "targeting digital signage offers during peak foot traffic periods to maximize conversion." },
    { id: 11, icon: "💎", title: "Sponsored Items", header: "Partner Boost: ", desc: "Prioritize SKUs from supplier trade agreements or \"Captain of Category\" partnerships on digital screens." },
    { id: 12, icon: "📊", title: "Stock Logic", header: "Smart Supply: ", desc: "Prioritize overstock items for promotions and automatically hide out-of-stock SKUs from digital playlists." },
    { id: 13, icon: "💡", title: "Contextual Suggestions", header: "Dynamic Cross-Sell: ", desc: "Suggest complementary products (e.g., pasta sauce) when shoppers dwell near specific aisles." },
    { id: 14, icon: "⏰", title: "Time of Day", header: "Dayparting: ", desc: "Automatically shift content strategies from Morning Energy to Evening Relaxation based on store hours." },
    { id: 15, icon: "⚽", title: "Sport Events", header: "Live Sync: ", desc: "Trigger promotions based on local team scores or major sporting events." },
  ];

  // Toggle individual card
  const toggle = (id) => {
    setMode('manual'); // Switching to manual if user clicks a specific box
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Set Manual Mode (Clear all or keep current - chose clear to allow fresh selection)
  const setManualMode = () => {
    setMode('manual');
    setSelected({}); // Optional: Remove this line if you want to keep selections when switching to manual
  };

  // Set Auto Mode (Select All)
  const setAutoMode = () => {
    setMode('auto');
    const allSelected = {};
    tools.forEach(tool => {
      allSelected[tool.id] = true;
    });
    setSelected(allSelected);
  };

  return (
    <section id="innovation" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-blue-900">Choose your AI Strategy</h2>
          <p className="mt-4 text-blue-600">Select multiple modules to activate intelligent, real-time adaptation.</p>
        </div>

        {/* Auto vs Manual Controls */}
        <div className="flex justify-center mb-10">
          <div className="bg-blue-50 p-1 rounded-lg border border-blue-100 flex shadow-sm">
            <button
              onClick={setManualMode}
              className={`px-6 py-2 rounded-md text-sm font-bold transition-all duration-200 ${
                mode === 'manual' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-blue-600 hover:bg-blue-100'
              }`}
            >
              Manual Selection
            </button>
            <button
              onClick={setAutoMode}
              className={`px-6 py-2 rounded-md text-sm font-bold transition-all duration-200 ${
                mode === 'auto' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-blue-600 hover:bg-blue-100'
              }`}
            >
              Auto AI ✨
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map(tool => (
            <div
                  key={tool.id}
                  onClick={() => toggle(tool.id)}
                  className={`cursor-pointer p-6 rounded-xl shadow-sm border transition-all duration-200 group relative ${
                    selected[tool.id]
                      ? "bg-blue-50 border-blue-900 ring-1 ring-blue-900" // Darker border & ring when selected
                      : "bg-white border-blue-300 hover:shadow-md"         // Slightly darker border when unselected
                  }`}
              >
                {/* Checkbox Circle */}
                <div
                  className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 transition-colors flex items-center justify-center ${
                    selected[tool.id]
                      ? "bg-blue-900 border-blue-900" // Darker circle background
                      : "border-blue-300 bg-white"    // Darker circle border
                  }`}
                >
                  <Check
                    size={14}
                    className={`text-white transition-opacity ${
                      selected[tool.id] ? "opacity-100" : "opacity-0"
                    }`}
                  />
                </div>

                <div className="text-3xl mb-4">{tool.icon}</div>
                
                {/* Updated Title Color to blue-950 */}
                <h3 className="text-lg font-bold text-blue-950 mb-2">{tool.title}</h3>
                
                <p className="text-sm text-blue-700 mb-4">
                  {/* Header styling: Dark blue (blue-950) and bold */}
                  <strong>{tool.header}</strong>{' '}
                  {/* Description styling: Standard blue-700 */}
                  {tool.desc}
                </p>

              {/* Special Slider Logic for External Retail Media */}
              {tool.hasSlider && (
                <div 
                  className="mt-3 pt-3 border-t border-blue-100" 
                  onClick={(e) => e.stopPropagation()} // Prevent clicking slider from toggling the card
                >
                  <div className="flex justify-between text-xs font-bold mb-2">
                    <span className="text-blue-600">External: <span>{sliderValue}%</span></span>
                    <span className="text-stone-400">Retailer: <span>{100 - sliderValue}%</span></span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={sliderValue}
                    onChange={(e) => setSliderValue(e.target.value)}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-200"
                    style={{
                      background: `linear-gradient(to right, #2563eb 0%, #2563eb ${sliderValue}%, #e5e7eb ${sliderValue}%, #e5e7eb 100%)`
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}